import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  automatedOnboardingCapabilitySchema,
  applicationCreationCapabilitySchema,
  entitlementResponseSchema,
  productAreaCensusSchema,
  productAreaListSchema,
  activationPreviewSchema,
  setupPreviewSchema,
  setupTransitionSchema,
  type ActivationPreview,
  type SetupTransition,
} from "./schemas";

export async function fetchAutomatedOnboardingCapability(tenantID: string) {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/oauth/dynamic-registration/capability`,
  );
  return automatedOnboardingCapabilitySchema.parse(raw);
}

export function useAutomatedOnboardingCapabilityQuery(tenantID: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.productAreas.automatedOnboarding(tenantID),
    queryFn: () => fetchAutomatedOnboardingCapability(tenantID),
    enabled,
  });
}

export async function fetchApplicationCreationCapability(tenantID: string) {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/applications/creation-capability`,
  );
  return applicationCreationCapabilitySchema.parse(raw);
}

export function useApplicationCreationCapabilityQuery(tenantID: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.productAreas.applicationCreation(tenantID),
    queryFn: () => fetchApplicationCreationCapability(tenantID),
    enabled,
  });
}

export async function fetchProductAreas(tenantID: string) {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/product-areas`,
  );
  return productAreaListSchema.parse(raw);
}

export async function fetchProductAreaCensus(tenantID: string) {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/product-areas/census`,
    { preservePlatformPath: true },
  );
  return productAreaCensusSchema.parse(raw);
}

export function useProductAreaCensusQuery(tenantID: string) {
  return useQuery<Awaited<ReturnType<typeof fetchProductAreaCensus>>, ApiError>({
    queryKey: queryKeys.productAreas.census(tenantID),
    queryFn: () => fetchProductAreaCensus(tenantID),
  });
}

export interface ProvisionEntitlementInput {
  area: string;
  status: "contracted" | "not_contracted" | "suspended";
  effective_from: string | null;
  effective_until: string | null;
  expected_version: number;
}

export function useProvisionEntitlementMutation(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ area, ...body }: ProvisionEntitlementInput) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(tenantID)}/product-areas/${encodeURIComponent(area)}/entitlement`,
          { method: "PUT", body, preservePlatformPath: true },
        ).then((raw) => entitlementResponseSchema.parse(raw)),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.productAreas.all(tenantID) });
    },
  });
}

// The setup preview and commit are tenant-facing, so they resolve to the
// tenant-host tree outside platform.* — the opposite of the census above.
export async function fetchSetupPreview(tenantID: string, area: string) {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/product-areas/${encodeURIComponent(area)}/setup/preview`,
  );
  return setupPreviewSchema.parse(raw);
}

export function useSetupPreviewQuery(tenantID: string, area: string) {
  return useQuery<Awaited<ReturnType<typeof fetchSetupPreview>>, ApiError>({
    queryKey: queryKeys.productAreas.setupPreview(tenantID, area),
    queryFn: () => fetchSetupPreview(tenantID, area),
    // The decision is only as good as the moment it was made, so it is never
    // served from cache across a revisit.
    staleTime: 0,
    gcTime: 0,
    // A focus change during passkey or uncertain-result handling must not swap
    // the operation identity underneath the reviewed request.
    refetchOnWindowFocus: false,
  });
}

export interface StartSetupInput {
  area: string;
  owner_subject: string;
  support_contact: string;
  /** Both versions come from the preview the human actually read. */
  expected_version: number;
  entitlement_version: number;
  /** Server-authored on the preview. Reused verbatim so a retry is recognized. */
  operation_ref: string;
}

export function useStartSetupMutation(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation<SetupTransition, ApiError, StartSetupInput>({
    mutationFn: ({ area, ...body }: StartSetupInput) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(tenantID)}/product-areas/${encodeURIComponent(area)}/setup`,
          { method: "POST", body },
        ).then((raw) => setupTransitionSchema.parse(raw)),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.productAreas.all(tenantID) });
    },
  });
}

/**
 * The activation preview is fetched on demand, when the switch is touched,
 * never cached: the decision is only as good as the moment it was made and
 * its operation reference is bound to the versions the human then reads.
 */
export async function fetchActivationPreview(tenantID: string, area: string, to: "on" | "off"): Promise<ActivationPreview> {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/product-areas/${encodeURIComponent(area)}/activation/preview?to=${to}`,
  );
  return activationPreviewSchema.parse(raw);
}

export interface SetActivationInput {
  area: string;
  activation: "on" | "off";
  /** Both versions come from the preview the human actually read. */
  expected_version: number;
  entitlement_version: number;
  /** Server-authored on the preview. Reused verbatim so a retry is recognized. */
  operation_ref: string;
}

export function useSetActivationMutation(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation<SetupTransition, ApiError, SetActivationInput>({
    mutationFn: ({ area, ...body }: SetActivationInput) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(tenantID)}/product-areas/${encodeURIComponent(area)}/activation`,
          { method: "POST", body },
        ).then((raw) => setupTransitionSchema.parse(raw)),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.productAreas.all(tenantID) });
      // Frontline off clears the offering switches server-side, and every
      // source row's people count and halves follow the areas' own tables.
      await queryClient.invalidateQueries({ queryKey: queryKeys.frontline.all(tenantID) });
    },
  });
}

export function useProductAreasQuery(tenantID: string, enabled = true) {
  return useQuery<Awaited<ReturnType<typeof fetchProductAreas>>, ApiError>({
    queryKey: queryKeys.productAreas.list(tenantID),
    queryFn: () => fetchProductAreas(tenantID),
    enabled,
    // Setup and administrators on other origins can change adoption without
    // invalidating this tab's cache. Keep every navigation consumer current.
    refetchInterval: 60_000,
    refetchOnWindowFocus: "always",
  });
}
